// Internal modules
import { fraudNet } from '@paypalcorp/c3-init-fraudnet';
import { appStore } from '@/store';
import { apolloClient, apolloQuery } from '@/lib/apollo';
import { getGlobals, getLocale, getToken } from '@/lib/globals';
import { CHECKOUT_SESSION_DATA_QUERY } from './graphql';
import { GRIFFIN_METADATA_QUERY } from '@/lib/graphql';
import {
  conditionallyUpdateShippingCountry,
  mapGriffinMetadata,
  prefillFormFields,
} from './utils';
import {
  getAddressMetaKey,
  getFraudNetConfig,
  handleApiError,
  logger,
  parseGqlError,
  TRANSACTION_DETAILS_PAYLOAD,
  validateGraphQLResponse,
} from '@/lib';

const initFraudNet = prefilledFields => {
  // Wrapping this in a try/catch because in the past we have seen occasional
  // errors with FraudNet being initialized which was blowing up the app
  try {
    const fraudNetConfig = getFraudNetConfig(prefilledFields);

    // `fraudNet()` is located in checkoutuinodeweb/shared directory
    fraudNet(fraudNetConfig);
    logger.info('weasley_fraud_net_init', fraudNetConfig);
  } catch (e) {
    logger.warn('weasley_fraud_net_init_failure', { message: e?.message });
  }
};

const fetchCheckoutSessionData = async queryOptions => {
  const token = getToken();
  const response = await apolloQuery({
    query: CHECKOUT_SESSION_DATA_QUERY,
    variables: { token },
    ...queryOptions,
  });

  /**
   * @TO_DO @TECH_DEBT
   * Temporarily leaving this check until we refactor our error handling
   */
  validateGraphQLResponse(
    response,
    'initial_session_data',
    'INITIAL_DATA_ERROR',
  );

  return response;
};

const fetchLocaleMetadata = (queryOptions, shippingCountryCode) => {
  const { country: countryCode, language: languageCode } = getLocale();

  return apolloClient
    .query({
      query: GRIFFIN_METADATA_QUERY,
      variables: { countryCode, languageCode, shippingCountryCode },
      ...queryOptions,
    })
    .then(response => {
      /**
       * @TO_DO @TECH_DEBT
       * Temporarily leaving this check until we refactor our error handling
       */
      validateGraphQLResponse(
        response,
        'initial_griffin_data',
        'INITIAL_DATA_ERROR',
      );

      return response;
    });
};

const cacheInitialDataInReduxStore = ({
  checkoutSessionResponse,
  localeMetadataResponse,
  shippingCountryCode,
}) => {
  const { localeMetadata: rawLocaleMetadata } = localeMetadataResponse.data;
  const { checkoutSession } = checkoutSessionResponse.data;
  const localeMetadata = mapGriffinMetadata(
    rawLocaleMetadata,
    shippingCountryCode,
  );
  const addressMeta = localeMetadata.address[getAddressMetaKey()];
  const shippingAddressMeta =
    localeMetadata.address[getAddressMetaKey(shippingCountryCode)];
  const { cart = {}, merchant = {} } = checkoutSession || {};
  const { total: cartAmount = {} } = cart?.amounts || {};
  const { amounts, description, intent, items, noteToBuyer } = cart;
  const cartDetails = {
    amounts,
    description,
    intent,
    items,
    merchant,
    noteToBuyer,
  };

  appStore.getActions().api.setGriffinMetadata(localeMetadata);

  // If it is a non-domestic transaction and the shipping country is
  // restricted to the merchant country, we update ship to billing to
  // `false` so the shipping fields will be shown
  conditionallyUpdateShippingCountry(merchant);

  // Actions to update the users information with the data passed
  // from the merchant
  prefillFormFields({
    addressMeta,
    cart,
    merchant,
    phoneMeta: localeMetadata.phoneMeta,
    shippingAddressMeta,
  });

  const { api: apiActions } = appStore.getActions();
  const prefilledAddressFields = Object.entries(cart.shippingAddress || {})
    .filter(([key, value]) => value && key !== 'isStoreAddress')
    .map(([key]) => key);

  apiActions.setPrefillAddressMetadata({
    hasPrefillAddress: Boolean(prefilledAddressFields.length),
  });
  apiActions.setCartData({
    allowedCardTypes: checkoutSession?.allowedCardIssuers || [],
    cancelUrl: cart.cancelUrl?.href || '',
    cartAmount,
    cartDetails,
    isShipToStore: Boolean(cart.shippingAddress?.isStoreAddress),
    shippingMethods: cart.shippingMethods || [],
  });
  apiActions.setMerchantId(checkoutSession?.merchant?.merchantId);
};

const fetchInitialData = () => {
  // For tracking total page load times
  logger.fptiTrackPageLoad.registerItem('initialData');
  logger.info('weasley_initial_data_call');
  const { ui: uiActions } = appStore.getActions();

  const { country: countryCode } = getLocale();
  const shippingCountryCode =
    getGlobals().initialGqlData?.checkoutSession?.cart?.shippingAddress
      ?.country ?? countryCode;

  return Promise.all([
    fetchLocaleMetadata({}, shippingCountryCode),
    fetchCheckoutSessionData(),
  ])
    .then(([localeMetadataResponse, checkoutSessionResponse]) => {
      cacheInitialDataInReduxStore({
        checkoutSessionResponse,
        localeMetadataResponse,
        shippingCountryCode,
      });
      uiActions.setIsReduxStoreHydrated(true);

      const { checkoutSession } = checkoutSessionResponse.data;
      const { cart } = checkoutSession;

      // We need to pass fields that are prefilled so FraudNet will ignore them.
      // If we don't do this FraudNet might tag them as being programmatically
      // entered and therefore potentially suspicious.
      const prefilledAddressFields = Object.entries(cart.shippingAddress || {})
        .filter(([key, value]) => value && key !== 'isStoreAddress')
        .map(([key]) => key);
      const prefilledNonAddressFields = [
        cart.email && 'email',
        cart.phoneNumber && 'phone',
      ].filter(f => f);

      logger.fptiTrackPageLoad.handleItemLoaded('initialData');

      const payload = TRANSACTION_DETAILS_PAYLOAD(checkoutSession);
      logger.fpti('weasley_transaction_details', payload);
      initFraudNet([...prefilledAddressFields, ...prefilledNonAddressFields]);

      logger.flush();
    })
    .catch(err => {
      const { error, message } = parseGqlError(err);

      logger.error('initial_data_err', {
        message,
        ...(err?.stack && { stack: err.stack }),
      });
      handleApiError(error);
    });
};

export {
  cacheInitialDataInReduxStore,
  fetchCheckoutSessionData,
  fetchLocaleMetadata,
  fetchInitialData,
};
